// Added by Sejune Oh on 2019-05-17
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component, createRef, RefObject } from 'react'
import { observer, inject } from 'mobx-react'
import { observable, action, computed, makeObservable } from 'mobx'
import localeCode from 'locale-code'
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import {
  UILanguageProps,
  InputBlurValidator,
  FORM_ID,
  InputValidator,
  SIGN_ERR_STATUS_CODE,
  MgmtValidator,
  ReactSelectEvent,
  TextFormatter,
} from '@/helper/define'

// Util
import { getFormData } from '@/helper/util'

// Intl
import Intl from '@/helper/Intl'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Component
import UserConfigDialog, { UserConfigDialogProps } from './UserConfigDialog'
import Dialog from '@/components/common/Dialog'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Store
import { UserAuthStore } from '@/stores/user/UserAuth'
import { UserStore, DisplableUserElementProps } from '@/stores/mgmt/user'
import withUserValidator from '../UserValidator'
import { UserBaseCProps } from '../UserBase/UserBaseContainer'
import { SystemConfigStore } from '@/stores/mgmt/config'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface UserConfigDialogCProps extends UserBaseCProps, MgmtValidator {
  userAuth?: UserAuthStore
  user?: UserStore
  systemConfig?: SystemConfigStore
  emailRef: RefObject<any>
  usernameRef: RefObject<any>
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
@inject('userAuth', 'user', 'systemConfig')
@observer
class UserConfigDialogContainer extends Component<UserConfigDialogCProps> {
  uiLanguage?: UILanguageProps
  mounted: boolean = false
  userElement?: DisplableUserElementProps

  _uiLanguageList: UILanguageProps[] = []
  _curPwdRef = createRef<any>()
  _newPwdRef = createRef<any>()
  _newConfirmPwdRef = createRef<any>()
  _dialogRef = createRef<Dialog>()

  constructor(props: UserConfigDialogCProps) {
    super(props)
    makeObservable(this, {
      uiLanguage: observable,
      mounted: observable,
      userElement: observable,
      componentDidMount: action,
      _setUILanguage: action,
      dialog: computed,
      userAuth: computed,
    })
  }

  async componentDidMount() {
    const { data: userAuthElement } = await this.props.userAuth!.getAuthenticatedUserInfo(
      this.userAuth.userInfo!.username!
    )
    let activated = false
    let description = ''

    await this.props.systemConfig!.initialize()
    this._uiLanguageList = this.props.systemConfig!.systemConfigsOption.languageConfig!.map((item) => {
      const nativeName = localeCode.getLanguageNativeName(item.lang_code)
      const countryCode = localeCode.getCountryCode(item.lang_code)
      const langCode = localeCode.getLanguageCode(item.lang_code)

      return {
        id: item.lang_code,
        label: `${nativeName} (${langCode}-${countryCode})`,
      }
    })

    if (!userAuthElement.external) {
      const { activated: userActivated, description: userDescription } = await this.props.user!.getElement(
        userAuthElement.id!
      )
      activated = userActivated!
      description = userDescription!
    }

    this.userElement = { ...userAuthElement, activated, description }
    const uiLang = this.userElement!.ui_language || this.userAuth.userInfo!.ui_language
    this.uiLanguage = this._uiLanguageList.find(({ id }) => id === uiLang)
    if (!this.uiLanguage && this._uiLanguageList.length > 0) {
      this.uiLanguage = this._uiLanguageList[0]
    }
    this.mounted = true
  }

  render() {
    const { validator: baseValidator, blurValidator: baseBlurValidator, ...others } = this.props
    const validator = {
      ...baseValidator,
      confirmPwdValidator: this._confirmPwdValidator,
      curPwdValidator: this._curPwdValidator,
    }
    const blurValidator = {
      ...baseBlurValidator,
      emailBlurValidator: this._emailBlurValidator,
      pwdBlurValidator: this._pwdBlurValidator,
      curPwdBlurValidator: this._curPwdBlurValidator,
      confirmPwdBlurValidator: this._confirmPwdBlurValidator,
    }
    const formatter = {
      uiLanguageFormatter: this._uiLanguageFormatter,
    }

    return (
      this.mounted && (
        <UserConfigDialog
          {...others}
          validator={validator}
          formatter={formatter}
          blurValidator={blurValidator}
          dialog={this.dialog}
          curPwdRef={this._curPwdRef}
          newPwdRef={this._newPwdRef}
          newConfirmPwdRef={this._newConfirmPwdRef}
          uiLanguage={this.uiLanguage!}
          userElement={this.userElement!}
          onUILanguageChange={this._handleUILanguageChange}
          uiLanguageList={this._uiLanguageList}
          isExternalUser={this.userAuth.isExternalUser!}
        />
      )
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _handleUILanguageChange: ReactSelectEvent = ({ target }) => {
    const langId = target.value
    const selectedLangId = this._uiLanguageList.find(({ id }) => id === langId)
    this._setUILanguage(selectedLangId!)
  }

  _updateUserInfo = () => {
    return new Promise(async (resolve) => {
      try {
        this._checkPasswords()
        const { noti_enabled, auth2_enabled } = await this.props.user!.getElement(this.userElement!.id as string)
        const userElement = {
          ...this.userElement!,
          ...(getFormData<DisplableUserElementProps>(
            document.getElementById(FORM_ID) as HTMLFormElement
            ) as DisplableUserElementProps),
            noti_enabled,
            auth2_enabled
        }

        delete userElement.current_pwd
        delete userElement.confirm_pwd
        delete userElement.created_at
        delete userElement.updated_at
        delete userElement.expired_at
        delete userElement.last_connected_at
        delete userElement.password

        await this.props.user!.updateElement(this.userElement!.id as string, userElement)
        await this._updateNewPassword()

        // @ts-ignore
        window.clapp.reload()

        resolve(true)
      } catch (err) {
        if (err.response) {
          const errMessage = err.response.data.message
          // 에러 메시지에 invalid current password 라는 문구가 포함되어 있으면 password가 잘못되었다는 메시지 출력
          if (errMessage && errMessage.includes('invalid current password')) {
            return this._curPwdRef.current!.setErrorState({
              validateStatus: true,
              errMessage: this.userAuth.getSignInErrMessage(Intl, 'wPwinfo')
            })
          }
          this._curPwdRef.current!.setErrorState({
            validateStatus: true,
            errMessage: this.userAuth.getSignInErrMessage(Intl, SIGN_ERR_STATUS_CODE[`${err.response.status}`]),
          })
        }
      }
    })
  }

  _updateNewPassword = async () => {
    const curPwd = this._curPwdRef!.current!
    const newPwd = this._newPwdRef!.current!
    const curConfirmPwd = this._newConfirmPwdRef!.current!

    if (curPwd.value !== '' || newPwd.value !== '' || curConfirmPwd.value !== '') {
      newPwd.callValidator()
      curConfirmPwd.callValidator()

      if (!newPwd.isErrorOccured && !curConfirmPwd.isErrorOccured) {
        return await this.props.user!.updatePassword({
          current_pwd: curPwd.value as string,
          password: this._newPwdRef.current!.value as string,
          id: this.userElement!.id!,
        })
      }

      return Promise.reject()
    }

    return Promise.resolve(true)
  }

  _checkPasswords = () => {
    const curPassword = this._curPwdRef.current!
    const newPassword = this._newPwdRef.current!
    const newConfirmPassword = this._newConfirmPwdRef.current!

    if (curPassword.value === '' && newPassword.value === '' && newConfirmPassword.value === '') {
      curPassword.setErrorState({ validateStatus: false })
      newPassword.setErrorState({ validateStatus: false })
      newConfirmPassword.setErrorState({ validateStatus: false })
    }
  }

  _confirmPwdValidator: InputValidator = (value) => {
    const curPwd = this._newPwdRef.current!.value

    if (!value) {
      const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialogcontainer.msg_not_empty' })
      return [true, eMessage]
    }

    if (curPwd !== value) {
      // prettier-ignore
      const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialogcontainer.msg_pwd_not_matched' })
      return [true, eMessage]
    }

    return [false]
  }

  _curPwdValidator: InputValidator = (value) => {
    if (!value) {
      const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialogcontainer.msg_not_empty' })
      return [true, eMessage]
    }
    
    return [false]
}

  _curPwdBlurValidator: InputBlurValidator = async (value) => {
    if (!value) {
      const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialogcontainer.msg_not_empty' })
      return [true, eMessage]
    }
    
    return [false]
  }


  _emailBlurValidator: InputBlurValidator = async (value) => {
    const pwdRef = this._newPwdRef.current!
    if (pwdRef.value !== '') pwdRef.callValidator()

    if (this.userElement!.email === value) {
      return [false]
    }

    return this.props.blurValidator!.emailBlurValidator(value)
  }

  _pwdBlurValidator: InputBlurValidator = async (value) => {
    if (!value) {
      const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialogcontainer.msg_not_empty' })
      return [true, eMessage]
    }
    if (this._newConfirmPwdRef.current) this._newConfirmPwdRef.current!.callValidator()
    return [false]
  }

  _confirmPwdBlurValidator: InputBlurValidator = async (value) => {
    const curPwd = this._newPwdRef!.current!.value
    this._curPwdRef.current!.callValidator()
    if (!value) {
      const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialogcontainer.msg_not_empty' })
      return [true, eMessage]
    }

    if (curPwd !== value) {
      // prettier-ignore
      const eMessage = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialogcontainer.msg_pwd_not_matched' })
      return [true, eMessage]
    }

    return [false]
  }

  _uiLanguageFormatter: TextFormatter<UILanguageProps> = (item: UILanguageProps) => {
    return `${item.label}`
  }

  openDialog = () => {
    if (this.userAuth.isExternalUser) {
      this._dialogRef.current!.setConfirmBtnDisabled(true)
    }

    const title = Intl.getIntlText({ id: 'htmljs_react.mgmt.userconfigdialogcontainer.dialog_title_edit' })

    this._dialogRef
      .current!.setDialogInfo({
        title,
        disableBackdropClick: true,
      })
      .handleOpen()
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  _setUILanguage = (uiLang: UILanguageProps) => {
    this.uiLanguage = uiLang
  }

  get dialog() {
    return {
      ref: this._dialogRef,
      onCancel: () => new Promise((resolve) => resolve(true)),
      onConfirm: this._updateUserInfo,
    } as UserConfigDialogProps
  }

  get userAuth() {
    return this.props.userAuth!
  }
}

export default withUserValidator()(UserConfigDialogContainer)
